"use client"

import { useEffect } from 'react'
import { usePostHog } from 'posthog-js/react'
import { decodeClickContextUnverified, readClickContextFromLocation } from '@/lib/click-context-decode'
import { allowSearchAnalytics, safeSearchProperties } from '@/lib/search/analytics-privacy'

interface ProductTrackingProps {
  productId: string
  productName: string
  productType: string
  productBrand: string
  priceCents?: number
  retailerName?: string
  source: 'product_page' | 'collection_page'
}

export function ProductTracking({
  productId,
  productName,
  productType,
  productBrand,
  priceCents,
  retailerName,
  source,
}: ProductTrackingProps) {
  const posthog = usePostHog()

  useEffect(() => {
    if (posthog && productId) {
      // FRIDGE-818: the channel the visitor arrived from, read (unverified —
      // the page holds no secret) from the `?cx=` click context on the URL.
      // The verified record is the /r/ click row.
      const cx = readClickContextFromLocation()
      const ctx = decodeClickContextUnverified(cx)
      // Track product view in PostHog with enhanced properties
      posthog.capture('product_viewed', {
        product_id: productId,
        product_name: productName,
        product_type: productType,
        product_brand: productBrand,
        price_cents: priceCents,
        retailer: retailerName,
        source: source,
        channel: ctx?.channel,
        channel_surface: ctx?.surface,
        channel_tool: ctx?.tool,
        channel_position: ctx?.position ?? undefined,
        channel_fin: ctx?.fin ?? undefined,
        channel_trace: ctx?.trace ?? undefined,
        channel_verified: ctx ? false : undefined,
        // User journey tracking
        $set_once: {
          first_product_viewed: productId,
          first_product_type: productType,
        },
        $set: {
          last_product_viewed: productId,
          last_product_type: productType,
          last_product_brand: productBrand,
        }
      })
    }
  }, [posthog, productId, productName, productType, productBrand, priceCents, retailerName, source])

  return null
}

/**
 * Track product click (use in onClick handlers)
 */
export function useProductClick() {
  const posthog = usePostHog()
  
  return (productId: string, position: number, source: string, productName?: string, productType?: string, priceCents?: number) => {
    if (posthog) {
      posthog.capture('product_clicked', {
        product_id: productId,
        product_name: productName,
        product_type: productType,
        price_cents: priceCents,
        position,
        source,
        $set: {
          last_clicked_product: productId,
          last_click_source: source,
        }
      })
    }
  }
}

/**
 * Track affiliate button click (before redirect)
 */
export function useAffiliateClick() {
  const posthog = usePostHog()
  
  return (productId: string, retailerName: string, priceCents?: number, productName?: string) => {
    if (posthog) {
      posthog.capture('affiliate_button_clicked', {
        product_id: productId,
        product_name: productName,
        retailer: retailerName,
        price_cents: priceCents,
        $set: {
          last_affiliate_retailer: retailerName,
          last_affiliate_click: new Date().toISOString(),
        }
      })
    }
  }
}

/**
 * Track comparison added (use when adding to comparison bar)
 */
export function useComparisonTracking() {
  const posthog = usePostHog()
  
  return (productId: string, productName?: string) => {
    if (posthog) {
      posthog.capture('comparison_added', {
        product_id: productId,
        product_name: productName,
      })
    }
  }
}

/**
 * Track search performed
 */
export function useSearchTracking() {
  const posthog = usePostHog()
  
  return (query: string, resultsCount: number, searchType: 'text' | 'image' | 'voice' = 'text') => {
    if (posthog && typeof window !== 'undefined' && allowSearchAnalytics({
      hostname: window.location.hostname,
      pathname: window.location.pathname,
      userAgent: navigator.userAgent,
      automated: navigator.webdriver,
    })) {
      posthog.capture('search_performed', {
        ...safeSearchProperties(query, resultsCount),
        search_type: searchType,
      })
    }
  }
}
